import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { Icon, Text, Button } from "@amzn/storm-ui";
import { questionCircleOutline, close } from "@amzn/storm-ui-icons";
import { useAsync, useIntervalEffect } from "@react-hookz/web";
import { isEmpty } from "lodash";
import React from "react";
import { useNavigate } from "react-router";
import * as R from "remeda";
import styled from "styled-components";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { ADVERTISER_ID_REQUEST_PARAMETER } from "../../config/constants";
import { Paths } from "../../constants/paths";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../constants/telemetry";
import { useGetRequestParameters } from "../../hooks/dimension/dimension.hooks";
import { useAsyncErrorStatusNotification } from "../../hooks/notifications/useAsyncErrorStatusNotification";
import { dimensionValueStore } from "../../store/dimension.value";
import { windowStore } from "../../store/window";
import { getStateKeys } from "../../store/zustand.helpers";
import { TabQueryParamValue } from "../../types/navigation";
import { isLoading } from "../../utils/api.utils";
import { IHubQueryParam, getAdvertiserId } from "../../utils/permission.utils";
import { logWithTakt } from "../../utils/telemetry.utils";
import { getSearchParams } from "../../utils/url.utils";

const Topbar = styled.div`
    position: sticky;
    top: 0;
    height: 45px;
    padding: 10px 25px;
    background-color: #fbfbfc;
    border-bottom: 2px solid #d9dee4;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
`;

const TopbarGrouping = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

export interface ActionBarProps {
    domainIdentifier: string;
    templateIdentifier: string;
}

/**
 * @deprecated
 */
export const ActionBar: React.FunctionComponent<ActionBarProps> = ({
    domainIdentifier,
    templateIdentifier,
}) => {
    const executionId = React.useRef("");
    const advertiserId = getAdvertiserId();

    // Needed because it may take a few calls before success
    const [isCreating, setIsCreating] = React.useState<boolean>(false);

    const [dimensionsIds] = useStore(dimensionValueStore, (state) => [
        getStateKeys(state),
    ]);
    const [pathname] = useStore(windowStore, (state) => [state.pathname]);

    const currentRequestParameters = useGetRequestParameters(dimensionsIds);

    const navigate = useNavigate();

    const intervalRefreshRateMS = 2000; // 2 seconds;

    const [generationState, generationAction] = useAsync(async () => {
        if (!isEmpty(executionId.current)) {
            await (
                await getCdbApiClient()
            ).getGeneratedInsight({
                advertiserId,
                executionId: executionId.current,
            });
            setIsCreating(false);
            navigate(
                `${Paths.HOME}${getSearchParams({ [IHubQueryParam.INITIAL_TAB]: TabQueryParamValue.GENERATED_INSIGHTS }, [IHubQueryParam.INITIAL_TAB])}`,
            );
        }
    });
    useAsyncErrorStatusNotification(generationState, ["NotFoundException"]);

    useIntervalEffect(() => {
        void generationAction.execute();
    }, intervalRefreshRateMS);

    const [packagedInsightState, packagedInsightAction] = useAsync(async () => {
        const data = await (
            await getCdbApiClient()
        ).createPackagedInsight({
            advertiserId,
            domainIdentifier,
            templateIdentifier,
            parameters: R.merge(
                ADVERTISER_ID_REQUEST_PARAMETER,
                currentRequestParameters,
            ),
            origin: `${origin}${pathname}${getSearchParams({ [IHubQueryParam.INITIAL_TAB]: TabQueryParamValue.GENERATED_INSIGHTS }, [IHubQueryParam.INITIAL_TAB])}`,
        });
        executionId.current = data.executionId!;
        setIsCreating(true);
        void generationAction.execute();
        logWithTakt(TELEMETRY_ID.CUSTOM_INSIGHT_REQUEST, {
            executionId: data.executionId!,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.CUSTOM_INSIGHT_REQUEST,
            PHOENIX_FEATURE.DISCOVERY,
            {
                customData: {
                    executionId: data.executionId!,
                },
            },
        );
    });
    useAsyncErrorStatusNotification(packagedInsightState);

    return (
        <Topbar>
            <TopbarGrouping>
                <Icon
                    data-testid="create-insight-action-exit"
                    type={close}
                    onClick={() =>
                        navigate(`${Paths.HOME}${getSearchParams()}`)
                    }
                    style={{ cursor: "pointer" }}
                />
                <Text renderAs="span" size="large">
                    New Insight
                </Text>
            </TopbarGrouping>
            <TopbarGrouping>
                <Button
                    variant="transparent"
                    data-testid="create-insight-cancel"
                    onClick={() =>
                        navigate(`${Paths.HOME}${getSearchParams()}`)
                    }
                >
                    Cancel
                </Button>
                <Button
                    data-testid="create-packaged-insight"
                    type="submit"
                    variant="primary"
                    loading={
                        isLoading(packagedInsightState.status) ||
                        isLoading(generationState.status) ||
                        isCreating
                    }
                    onClick={() => void packagedInsightAction.execute()}
                    disabled={isEmpty(templateIdentifier)}
                >
                    Generate Packaged Insight
                </Button>
                <Icon
                    type={questionCircleOutline}
                    style={{ cursor: "pointer" }}
                />
            </TopbarGrouping>
        </Topbar>
    );
};
